import deleteUserApi from '@/api/delete/user/delete-user';
import getUsersApi from '@/api/get/user-management/get-users';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import TabPortal from '@/components/page-tabs/page-tab-portal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import Pill from '@/components/pill/pill-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { APP_CONTEXT } from '@/utils/context';
import { useCallback, useContext, useState, useEffect } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const renderStatus = ({ row }) => (
  <StatusIndicator status={row.original.status.toLowerCase()} />
);

const renderType = ({ row }) => (
  <Pill type={row.original.user_type.toLowerCase()} />
);

/**
 * User Management page component.
 * @returns {JSX.Element} The rendered User Management page component.
 */
const UserAll = () => {
  const tableId = 'user-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const navigate = useNavigate();
  const { isSuper } = useContext(APP_CONTEXT);
  const { data, refetch, error, loading, count } = useLazyQuery(getUsersApi);
  const [deleteUser, { loading: deleteUserLoading }] =
    useMutation(deleteUserApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const launchDeleteConfirmation = useCallback(
    (userData) => {
      ConfirmationModal({
        title: 'Delete User',
        text: `Are you sure you want to delete ${userData.values.first_name} ${userData.values.last_name}?`,
        onConfirm: () =>
          deleteUser(userData?.original?.id).then(() => {
            Alert({
              icon: 'success',
              text: 'User deleted successfully',
            });
            refetch();
          }),
      });
    },
    [deleteUser, refetch],
  );

  const handleEdit = useCallback(
    (row) => {
      const userDetails = row.original;
      navigate(`edit/${userDetails.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const handleView = useCallback(
    (row) => {
      const userDetails = row.original;
      navigate(`view/${userDetails.id}`);
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="user"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        onView={() => handleView(row)}
        onDuplicate={null}
      />
    ),
    [handleDelete, handleEdit, handleView],
  );

  const renderLastLogin = ({ row }) => {
    if (row.original.last_login) {
      const date = row.original.last_login;
      const formattedDate = dayjs(date).format('DD/MM/YYYY');
      const formattedTime = dayjs(date).format('hh:mm');
      return <div>{`${formattedDate} | ${formattedTime}`}</div>;
    }
    return <div />;
  };

  const filters = [
    {
      header: 'User Type:',
      accessor: 'user_type',
      placeholder: 'Please select a User Type',
      type: 'select',
      options: [
        { value: 'company', text: 'Company' },
        { value: 'user', text: 'User' },
      ],
    },
  ];

  const UserColumns = [
    {
      Header: 'FIRST NAME',
      id: 'first_name',
      accessor: 'first_name',
      search: true,
    },
    {
      Header: 'LAST NAME',
      id: 'last_name',
      accessor: 'last_name',
      search: true,
    },
    {
      Header: 'USER EMAIL',
      id: 'email',
      accessor: 'email',
      search: true,
    },
    {
      Header: 'USER ROLE',
      id: 'user_role_name',
      accessor: 'user_role_name',
      search: true,
    },
    {
      Header: 'USER TYPE',
      id: 'user_type',
      accessor: 'user_type',
      search: true,
      Cell: renderType,
    },
    {
      Header: 'LAST LOGIN',
      id: 'last_login',
      accessor: 'last_login',
      search: true,
      Cell: (props) => renderLastLogin(props),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: renderStatus,
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  if (isSuper) {
    UserColumns.splice(2, 0, {
      Header: 'COMPANY',
      id: 'company_name',
      accessor: 'company_name',
      search: true,
    });
  }

  return (
    <PageWrapper>
      <TabPortal>
        <PermissionWrapper permissions={['user.create']}>
          <PrimaryButton
            title="New User"
            onClick={() => navigate('new')}
            buttonHeight="40px"
            icon={<FiEdit />}
          />
        </PermissionWrapper>
      </TabPortal>
      <Table
        feature="user"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleDelete(row)}
        onRowView={(row) => handleView(row)}
        columns={UserColumns}
        data={data ?? []}
        tableOptions={['search', 'filter', 'columns']}
        id={tableId}
        defaultSort={defaultSort}
        defaultVariables={defaultVariables}
        dataCount={count ?? 0}
        downloadAccessor={data || []}
        error={error}
        loading={deleteUserLoading || loading || fetchingPresets}
        filters={filters}
        refetch={refetch}
        getDownloadData={getUsersApi}
      />
    </PageWrapper>
  );
};

export default UserAll;
